import {
  GalleryImageItem,
  isGalleryEmbedItem,
  isGalleryVideoItem,
} from "@/lib/image";
import { ResponsiveVideo } from "@/components/media/ResponsiveVideo";
import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import { CATALOG_FULLSCREEN_PLAYER_OVERRIDES } from "@/lib/media/catalogFullscreenPlayer";
import { cn } from "@/lib/utils";
import { Cross2Icon } from "@radix-ui/react-icons";
import { ChevronLeft, ChevronRight, Play, Youtube } from "lucide-react";
import React from "react";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

interface IMediaOrVideoProps {
  media: {
    url: string;
    largeUrl: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    kind?: GalleryImageItem["kind"];
    videoUrl?: string | null;
    embedProvider?: string;
    embedVideoId?: string;
    embedUrl?: string;
  };
  /** Toutes les photos de la fiche ; active la navigation si length > 1. */
  gallery?: GalleryImageItem[];
  /** Index dans `gallery` à l'ouverture (défaut : 0). */
  initialIndex?: number;
}

export const UniqueImageFullScreen = ({
  media: {
    url,
    largeUrl,
    alt,
    width,
    height,
    className,
    kind,
    videoUrl,
    embedProvider,
    embedVideoId,
    embedUrl,
  },
  gallery,
  initialIndex = 0,
}: IMediaOrVideoProps) => {
  const vt = useVisibleT();
  const [open, setOpen] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);

  const items = React.useMemo<GalleryImageItem[]>(() => {
    if (gallery && gallery.length > 0) {
      return gallery;
    }
    return [
      {
        id: "single",
        url,
        largeUrl,
        alt,
        kind,
        videoUrl,
        embedProvider,
        embedVideoId,
        embedUrl,
      },
    ];
  }, [
    gallery,
    url,
    largeUrl,
    alt,
    kind,
    videoUrl,
    embedProvider,
    embedVideoId,
    embedUrl,
  ]);

  const canNavigate = items.length > 1;
  const current = items[currentIndex] ?? items[0];
  const currentIsVideo = isGalleryVideoItem(current);
  const currentIsEmbed = isGalleryEmbedItem(current);

  const goPrevious = React.useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setCurrentIndex((prev) =>
        prev === 0 ? items.length - 1 : prev - 1,
      );
    },
    [items.length],
  );

  const goNext = React.useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setCurrentIndex((prev) =>
        prev === items.length - 1 ? 0 : prev + 1,
      );
    },
    [items.length],
  );

  React.useEffect(() => {
    if (open) {
      const safeIndex =
        initialIndex >= 0 && initialIndex < items.length ? initialIndex : 0;
      setCurrentIndex(safeIndex);
    }
  }, [open, initialIndex, items.length]);

  React.useEffect(() => {
    if (!open || !canNavigate) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, canNavigate, goPrevious, goNext]);

  const triggerIsVideo = isGalleryVideoItem({ kind, videoUrl });
  const triggerIsEmbed = kind === "embed";

  const littleContent =
    triggerIsVideo || triggerIsEmbed ? (
      <div
        className={cn(
          "relative flex h-full w-full cursor-pointer items-center justify-center overflow-hidden",
          className,
        )}
      >
        {url ? (
          <img
            draggable={false}
            src={url}
            alt={alt}
            width={width || 500}
            height={height || 500}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-black/80">
            <Youtube className="h-10 w-10 text-white" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/55">
            <Play className="h-5 w-5 fill-white text-white pl-0.5" />
          </span>
        </div>
      </div>
    ) : (
      <img
        draggable={false}
        src={url || ""}
        alt={alt}
        width={width || 500}
        height={height || 500}
        className={`object-contain cursor-pointer ${className}`}
      />
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild className="relative h-full w-full">
        {littleContent}
      </DialogTrigger>
      <DialogContent
        overlay="full"
        className="bg-transparent border-none shadow-none h-[95dvh] min-w-[90dvw] max-w-[95dvw] p-0 flex items-center justify-center outline-none"
        onInteractOutside={() => setOpen(false)}
        onEscapeKeyDown={() => setOpen(false)}
        needCloseButton={false}
      >
        <div
          className="relative flex h-full w-full items-center justify-center"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          {canNavigate && (
            <button
              type="button"
              onClick={goPrevious}
              className="absolute left-2 z-10 rounded-full p-2 text-white bg-transparent sm:left-4"
              aria-label={vt("bo.modal.previousPhoto", "Photo précédente")}
            >
              <ChevronLeft className="h-10 w-10" />
            </button>
          )}

          <div
            className="relative max-h-[90dvh] max-w-[85dvw] p-4"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            {currentIsVideo && current.videoUrl ? (
              <ResponsiveVideo
                src={current.videoUrl}
                poster={current.url || undefined}
                className="max-h-[90dvh] w-full bg-black"
              />
            ) : currentIsEmbed ? (
              <div className="h-[80dvh] w-[85dvw]">
                <EmbedPlayer
                  contain
                  eager
                  provider={current.embedProvider}
                  videoId={current.embedVideoId}
                  embedUrl={current.embedUrl}
                  className="h-full w-full"
                  title={current.alt || alt}
                  playerOverrides={CATALOG_FULLSCREEN_PLAYER_OVERRIDES}
                />
              </div>
            ) : (
              <img
                draggable={false}
                src={current.largeUrl || current.url}
                alt={current.alt || alt}
                className="max-h-[90dvh] w-full object-contain"
              />
            )}
          </div>

          {canNavigate && (
            <button
              type="button"
              onClick={goNext}
              className="absolute right-2 z-10 rounded-full p-2 text-white bg-transparent sm:right-4"
              aria-label={vt("bo.modal.nextPhoto", "Photo suivante")}
            >
              <ChevronRight className="h-10 w-10" />
            </button>
          )}

          <button
            type="button"
            className="absolute right-4 top-4 z-10 text-white bg-transparent hover:text-gray-300"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-label={vt("bo.modal.close", "Fermer")}
          >
            <Cross2Icon className="h-8 w-8" />
            <span className="sr-only">Close</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
